import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { capitalCase, noCase } from 'change-case'

import { Modal } from '@/lib/core/components/Modal'
import { H3 } from '@/lib/core/components/Text'
import { Button } from '@/lib/core/components/Button'
import { Link } from '@/lib/core/components/Link'
import { DropzoneField } from '@/lib/core/components/DropzoneField'
import { Label } from '@/lib/core/components/TextField/TextField.styles'

import styles from '../CreateTaskModal/CreateTaskModal.module.css'
import { importTasks } from '../../api'

export interface ImportTasksModalProps {
  isActive: boolean
  onClose: () => void
  onImported?: () => void
}

type ImportError = { row: number; message: object }
type ImportResponseData = { errorRows: ImportError[]; createdRows: number; updatedRows: number }

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5MB
const ALLOWED_UPLOAD_EXTENSIONS = ['csv']

export const ImportTasksModal = ({ isActive, onClose, onImported }: ImportTasksModalProps) => {
  const formMethods = useForm<{ files: FileList }>()
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [createdCount, setCreatedCount] = useState<number>(0)
  const [updatedCount, setUpdatedCount] = useState<number>(0)
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [submittedFile, setSubmittedFile] = useState<File | null>(null)
  const [isSuccessImported, setIsSuccessImported] = useState<boolean>(false)

  const { handleSubmit, reset, watch } = formMethods

  const selectedFiles = watch('files')

  const enableUploadButton = useMemo(() => {
    if (selectedFiles?.length === 1) {
      const file = selectedFiles[0]
      const fileName = file.name
      const fileExtension = fileName.split('.').pop()?.toLowerCase() ?? ''
      if (ALLOWED_UPLOAD_EXTENSIONS.includes(fileExtension) && file.size <= MAX_FILE_SIZE_BYTES) {
        return true
      }
    }
    return false
  }, [selectedFiles])

  const onSubmit = useCallback(
    async (data: { files: FileList }) => {
      try {
        setIsImporting(true)
        setImportErrors([])
        setSubmittedFile(data.files[0])
        const formData = new FormData()
        formData.append('file', data.files[0])
        const response = await importTasks(formData)
        const responseData = response.data as ImportResponseData

        if (responseData.errorRows.length > 0) {
          setCreatedCount(responseData.createdRows)
          setUpdatedCount(responseData.updatedRows)
          const errors = responseData.errorRows.map((error: ImportError) => {
            let errorMessage = 'Invalid values' // General error message
            for (const [key, value] of Object.entries(error.message)) {
              errorMessage = `${capitalCase(noCase(key))} - ${value}`
            }

            return `Row ${error['row']} - ${errorMessage}`
          })
          setImportErrors(errors)
          reset()
        } else {
          setIsSuccessImported(true)
          reset()
          onImported?.()
        }
      } catch {
        setImportErrors(['Failed to create task. Please try again.'])
      } finally {
        setIsImporting(false)
      }
    },
    [reset, onImported, onClose],
  )

  const handleCancel = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  // Reset form when modal is closed
  useEffect(() => {
    if (!isActive) {
      reset()
      setSubmittedFile(null)
      setIsSuccessImported(false)
      setImportErrors([])
      setCreatedCount(0)
      setUpdatedCount(0)
    }
  }, [isActive, reset])

  return (
    <Modal
      isActive={isActive}
      actions={
        <>
          {isSuccessImported ? (
            <Button onClick={handleCancel} variant="ghost" size="large">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel} variant="ghost" size="large">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                variant="secondary"
                size="large"
                disabled={!enableUploadButton || isImporting}
                isLoading={isImporting}
                type="submit"
              >
                Upload
              </Button>
            </>
          )}
        </>
      }
    >
      <H3>Upload Tasks</H3>
      {importErrors.length > 0 && (
        <>
          <div className={styles.errorMessage}>
            Error uploading <strong>{submittedFile?.name}</strong>. Try again.
            <br />
            Created {createdCount} rows - Updated {updatedCount} rows
          </div>

          {importErrors.map((error) => (
            <div key={error} className={styles.errorMessage}>
              {error}
            </div>
          ))}
        </>
      )}

      {isSuccessImported && (
        <div className={styles.importedMessage}>
          Success uploading <strong>{submittedFile?.name}</strong>.
        </div>
      )}

      {!isSuccessImported && (
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.createTaskForm}>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Label>
                  Select a CSV file to upload.{' '}
                  <Link href="/example-files/Import-Task-Example.csv" download>
                    Download an example file
                  </Link>
                </Label>
                <br />
                <DropzoneField name="files" multiple={false} accept={{ 'text/csv': ['.csv'] }} />
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </Modal>
  )
}
